import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Users, FileText, Clock, Shield, Building2, UserPlus, AlertTriangle, CheckCircle,
  ChevronRight, Folder, FolderOpen, Plus, Search, Eye, Download, Calendar,
  Mail, Phone, BarChart3, Bell, History, Settings, Network, Briefcase
} from 'lucide-react';

// Recursive component for hierarchy tree - defined outside main component
const HierarchyNode = ({ node, level = 0 }) => (
  <div style={{ marginLeft: `${level * 24}px` }}>
    <div className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg">
      <Avatar className="h-8 w-8">
        <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
          {node.user?.first_name?.[0]}{node.user?.last_name?.[0]}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{node.user?.first_name} {node.user?.last_name}</p>
        <p className="text-xs text-muted-foreground">{node.job_title}</p>
      </div>
      <Badge variant="outline" className="text-xs">
        {node.hierarchy_level?.name}
      </Badge>
      {node.department && (
        <Badge className="bg-slate-100 text-slate-700 text-xs">{node.department.code}</Badge>
      )}
    </div>
    {node.direct_reports?.length > 0 && (
      <div className="ml-6 border-l-2 border-slate-200 pl-2">
        {node.direct_reports.map(report => (
          <HierarchyNode key={report.user_id} node={report} level={level + 1} />
        ))}
      </div>
    )}
  </div>
);

export default function HRHubPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Policies
  const [policyFolders, setPolicyFolders] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [policySearch, setPolicySearch] = useState('');
  const [showNewPolicyDialog, setShowNewPolicyDialog] = useState(false);
  const [newPolicy, setNewPolicy] = useState({ title: '', content: '', summary: '', policy_type: 'company', tags: [] });
  
  // Departments & Team
  const [departments, setDepartments] = useState([]);
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [hierarchyMap, setHierarchyMap] = useState([]);
  const [crossFunctionalTeams, setCrossFunctionalTeams] = useState([]);
  const [showNewDeptDialog, setShowNewDeptDialog] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', code: '', description: '' });
  
  // Time Off
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myBalance, setMyBalance] = useState(null);
  const [showNewTimeOffDialog, setShowNewTimeOffDialog] = useState(false);
  const [newTimeOff, setNewTimeOff] = useState({
    request_type: 'vacation',
    start_date: '',
    end_date: '',
    total_days: 1,
    reason: ''
  });
  
  // Security / Users
  const [userSeats, setUserSeats] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '', first_name: '', last_name: '', role: 'sales_rep', password: '',
    department_id: '', hierarchy_level_id: '', reports_to: '', job_title: ''
  });
  
  // HR Inbox
  const [hrInbox, setHrInbox] = useState([]);
  
  // Performance Alerts
  const [performanceAlerts, setPerformanceAlerts] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'policies') {
      fetchPolicies();
      fetchPolicyFolders();
    } else if (activeTab === 'team') {
      fetchDepartments();
      fetchHierarchyLevels();
      fetchTeamMembers();
      fetchHierarchyMap();
      fetchCrossFunctionalTeams();
    } else if (activeTab === 'time-off') {
      fetchTimeOffRequests();
      fetchPendingApprovals();
      fetchMyBalance();
    } else if (activeTab === 'security') {
      fetchUserSeats();
      fetchAuditLogs();
    } else if (activeTab === 'inbox') {
      fetchHRInbox();
    } else if (activeTab === 'performance') {
      fetchPerformanceAlerts();
    }
  }, [activeTab]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await api.get('/hr/dashboard');
      setDashboardData(res.data);
    } catch (error) {
      console.error('Failed to fetch HR dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPolicyFolders = async () => {
    try {
      const res = await api.get('/hr/policy-folders/tree');
      setPolicyFolders(res.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const params = {};
      if (selectedFolder) params.folder_id = selectedFolder;
      if (policySearch) params.search = policySearch;
      const res = await api.get('/hr/policies', { params });
      setPolicies(res.data);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/hr/departments');
      setDepartments(res.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchHierarchyLevels = async () => {
    try {
      const res = await api.get('/hr/hierarchy-levels');
      setHierarchyLevels(res.data);
    } catch (error) {
      console.error('Failed to fetch hierarchy levels:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await api.get('/hr/team-members');
      setTeamMembers(res.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchHierarchyMap = async () => {
    try {
      const res = await api.get('/hr/hierarchy-map');
      setHierarchyMap(res.data);
    } catch (error) {
      console.error('Failed to fetch hierarchy map:', error);
    }
  };

  const fetchCrossFunctionalTeams = async () => {
    try {
      const res = await api.get('/hr/cross-functional-teams');
      setCrossFunctionalTeams(res.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchTimeOffRequests = async () => {
    try {
      const res = await api.get('/hr/time-off/my-requests');
      setTimeOffRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch time off requests:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await api.get('/hr/time-off/pending-approvals');
      setPendingApprovals(res.data);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
    }
  };

  const fetchMyBalance = async () => {
    try {
      const res = await api.get('/hr/time-off/balance');
      setMyBalance(res.data);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const fetchUserSeats = async () => {
    try {
      const res = await api.get('/hr/security/users');
      setUserSeats(res.data);
    } catch (error) {
      console.error('Failed to fetch user seats:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await api.get('/hr/audit-logs', { params: { limit: 50 } });
      setAuditLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const fetchHRInbox = async () => {
    try {
      const res = await api.get('/hr/inbox');
      setHrInbox(res.data);
    } catch (error) {
      console.error('Failed to fetch HR inbox:', error);
    }
  };

  const fetchPerformanceAlerts = async () => {
    try {
      const res = await api.get('/hr/performance/alerts');
      setPerformanceAlerts(res.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const handleCreatePolicy = async () => {
    try {
      await api.post('/hr/policies', newPolicy);
      toast.success('Policy created');
      setShowNewPolicyDialog(false);
      setNewPolicy({ title: '', content: '', summary: '', policy_type: 'company', tags: [] });
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to create policy');
    }
  };

  const handleCreateDepartment = async () => {
    try {
      await api.post('/hr/departments', newDept);
      toast.success('Department created');
      setShowNewDeptDialog(false);
      setNewDept({ name: '', code: '', description: '' });
      fetchDepartments();
    } catch (error) {
      toast.error('Failed to create department');
    }
  };

  const handleCreateTimeOff = async () => {
    try {
      await api.post('/hr/time-off/requests', newTimeOff);
      toast.success('Time off request submitted');
      setShowNewTimeOffDialog(false);
      setNewTimeOff({ request_type: 'vacation', start_date: '', end_date: '', total_days: 1, reason: '' });
      fetchTimeOffRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  const handleApproveTimeOff = async (requestId, approve) => {
    try {
      await api.put(`/hr/time-off/requests/${requestId}`, {
        status: approve ? 'approved' : 'rejected',
        rejection_reason: approve ? null : 'Denied by manager'
      });
      toast.success(approve ? 'Request approved' : 'Request rejected');
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleCreateUser = async () => {
    try {
      await api.post('/hr/security/users', newUser);
      toast.success('User created');
      setShowNewUserDialog(false);
      setNewUser({
        email: '', first_name: '', last_name: '', role: 'sales_rep', password: '',
        department_id: '', hierarchy_level_id: '', reports_to: '', job_title: ''
      });
      fetchUserSeats();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    try {
      await api.put(`/hr/security/users/${userId}/suspend`);
      toast.success('User suspended');
      fetchUserSeats();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await api.put(`/hr/security/users/${userId}/reactivate`);
      toast.success('User reactivated');
      fetchUserSeats();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to reactivate user');
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-hub-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">HR Hub</h1>
          <p className="text-muted-foreground">Human Resources Management</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full max-w-4xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-1" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">
            <FileText className="h-4 w-4 mr-1" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">
            <Users className="h-4 w-4 mr-1" />
            Team
          </TabsTrigger>
          <TabsTrigger value="time-off" data-testid="tab-time-off">
            <Calendar className="h-4 w-4 mr-1" />
            Time Off
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="h-4 w-4 mr-1" />
            Security
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            <Mail className="h-4 w-4 mr-1" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">
            <Bell className="h-4 w-4 mr-1" />
            Alerts
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('time-off')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-amber-600">{dashboardData?.pending_time_off || 0}</p>
                    <p className="text-sm text-muted-foreground">Pending Time Off</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('inbox')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-blue-600">{dashboardData?.pending_inbox || 0}</p>
                    <p className="text-sm text-muted-foreground">HR Inbox Items</p>
                  </div>
                  <Mail className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('security')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-emerald-600">{dashboardData?.active_users || 0}</p>
                    <p className="text-sm text-muted-foreground">Active Users</p>
                  </div>
                  <Users className="h-8 w-8 text-emerald-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('performance')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-red-600">{dashboardData?.active_alerts || 0}</p>
                    <p className="text-sm text-muted-foreground">Performance Alerts</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Audit Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData?.recent_audits?.slice(0, 5).map(audit => (
                  <div key={audit.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {audit.user?.first_name?.[0]}{audit.user?.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{audit.user?.first_name} {audit.user?.last_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {audit.action} {audit.entity_type}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(audit.timestamp), { addSuffix: true })}
                    </span>
                  </div>
                ))}
                {(!dashboardData?.recent_audits || dashboardData.recent_audits.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search policies..."
                  value={policySearch}
                  onChange={(e) => setPolicySearch(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="policy-search"
                />
              </div>
            </div>
            <Dialog open={showNewPolicyDialog} onOpenChange={setShowNewPolicyDialog}>
              <DialogTrigger asChild>
                <Button data-testid="new-policy-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Policy
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Policy</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={newPolicy.title}
                      onChange={(e) => setNewPolicy({ ...newPolicy, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Summary</Label>
                    <Input
                      value={newPolicy.summary}
                      onChange={(e) => setNewPolicy({ ...newPolicy, summary: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Policy Type</Label>
                    <Select
                      value={newPolicy.policy_type}
                      onValueChange={(val) => setNewPolicy({ ...newPolicy, policy_type: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Company-wide</SelectItem>
                        <SelectItem value="department">Department</SelectItem>
                        <SelectItem value="group">Group</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea
                      rows={8}
                      value={newPolicy.content}
                      onChange={(e) => setNewPolicy({ ...newPolicy, content: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewPolicyDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreatePolicy}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Folder Tree */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base">Folders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${!selectedFolder ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'}`}
                    onClick={() => setSelectedFolder(null)}
                  >
                    <FolderOpen className="h-4 w-4" />
                    <span className="text-sm">All Policies</span>
                  </div>
                  {policyFolders.map(folder => (
                    <div
                      key={folder.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer ${selectedFolder === folder.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'}`}
                      onClick={() => setSelectedFolder(folder.id)}
                    >
                      <Folder className="h-4 w-4" />
                      <span className="text-sm">{folder.name}</span>
                      <Badge variant="outline" className="ml-auto text-xs">{folder.policy_count || 0}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Policies List */}
            <div className="lg:col-span-3 space-y-4">
              {policies.map(policy => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{policy.title}</h3>
                          <Badge className={
                            policy.policy_type === 'company' ? 'bg-blue-100 text-blue-700' :
                            policy.policy_type === 'department' ? 'bg-purple-100 text-purple-700' :
                            'bg-emerald-100 text-emerald-700'
                          }>
                            {policy.policy_type}
                          </Badge>
                          {policy.acknowledged_by_me && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Acknowledged
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{policy.summary || policy.content}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span>v{policy.version}</span>
                          <span>{policy.view_count} views</span>
                          <span>Updated {formatDistanceToNow(new Date(policy.updated_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {policies.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-muted-foreground">No policies found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Departments */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Departments
                </CardTitle>
                <Dialog open={showNewDeptDialog} onOpenChange={setShowNewDeptDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Department</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={newDept.name}
                          onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Code</Label>
                        <Input
                          value={newDept.code}
                          onChange={(e) => setNewDept({ ...newDept, code: e.target.value.toUpperCase() })}
                          placeholder="e.g., ENG, MKT, HR"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newDept.description}
                          onChange={(e) => setNewDept({ ...newDept, description: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowNewDeptDialog(false)}>Cancel</Button>
                      <Button onClick={handleCreateDepartment}>Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {departments.map(dept => (
                    <div key={dept.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{dept.code}</Badge>
                          <span className="font-medium">{dept.name}</span>
                        </div>
                        {dept.head && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Head: {dept.head.first_name} {dept.head.last_name}
                          </p>
                        )}
                      </div>
                      <Badge className="bg-slate-100 text-slate-700">{dept.member_count} members</Badge>
                    </div>
                  ))}
                  {departments.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No departments</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Hierarchy Levels */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="h-5 w-5" />
                  Hierarchy Levels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {hierarchyLevels.map((level, idx) => (
                    <div key={level.id} className="flex items-center gap-3 p-2 border rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                        {level.level}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{level.name}</p>
                        <p className="text-xs text-muted-foreground">{level.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Hierarchy Map */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Organization Hierarchy
              </CardTitle>
              <CardDescription>Reporting structure and team alignment</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {hierarchyMap.map(node => (
                  <HierarchyNode key={node.user_id} node={node} />
                ))}
                {hierarchyMap.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No hierarchy configured. Add team members to build the org chart.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Cross-Functional Teams */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Cross-Functional Teams
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {crossFunctionalTeams.map(team => (
                  <div key={team.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h4 className="font-semibold">{team.name}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">{team.description}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex -space-x-2">
                        {team.members?.slice(0, 3).map((member, i) => (
                          <Avatar key={i} className="h-6 w-6 border-2 border-white">
                            <AvatarFallback className="text-xs">{member.first_name?.[0]}{member.last_name?.[0]}</AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">{team.members?.length || 0} members</span>
                      {team.pending_count > 0 && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">{team.pending_count} pending</Badge>
                      )}
                    </div>
                  </div>
                ))}
                {crossFunctionalTeams.length === 0 && (
                  <p className="text-muted-foreground col-span-full text-center py-4">No cross-functional teams</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Off Tab */}
        <TabsContent value="time-off" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Time Off Management</h2>
            <Dialog open={showNewTimeOffDialog} onOpenChange={setShowNewTimeOffDialog}>
              <DialogTrigger asChild>
                <Button data-testid="new-time-off-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Request Time Off
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Time Off</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={newTimeOff.request_type}
                      onValueChange={(val) => setNewTimeOff({ ...newTimeOff, request_type: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vacation">Vacation</SelectItem>
                        <SelectItem value="sick">Sick Leave</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="bereavement">Bereavement</SelectItem>
                        <SelectItem value="parental">Parental Leave</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={newTimeOff.start_date}
                        onChange={(e) => setNewTimeOff({ ...newTimeOff, start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={newTimeOff.end_date}
                        onChange={(e) => setNewTimeOff({ ...newTimeOff, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Total Days</Label>
                    <Input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newTimeOff.total_days}
                      onChange={(e) => setNewTimeOff({ ...newTimeOff, total_days: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Reason (optional)</Label>
                    <Textarea
                      value={newTimeOff.reason}
                      onChange={(e) => setNewTimeOff({ ...newTimeOff, reason: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewTimeOffDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateTimeOff}>Submit Request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Balance Card */}
            {myBalance && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">My Balance ({myBalance.year})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Vacation</span>
                    <span className="font-medium">{myBalance.vacation_total - myBalance.vacation_used} / {myBalance.vacation_total} days</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Sick</span>
                    <span className="font-medium">{myBalance.sick_total - myBalance.sick_used} / {myBalance.sick_total} days</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Personal</span>
                    <span className="font-medium">{myBalance.personal_total - myBalance.personal_used} / {myBalance.personal_total} days</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* My Requests */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">My Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {timeOffRequests.map(req => (
                    <div key={req.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">{req.request_type}</Badge>
                          <span className="font-medium">{req.total_days} days</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(req.start_date), 'MMM d')} - {format(new Date(req.end_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Badge className={
                        req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        req.status === 'cancelled' ? 'bg-slate-100 text-slate-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {req.status}
                      </Badge>
                    </div>
                  ))}
                  {timeOffRequests.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No requests</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Approvals (for managers) */}
          {pendingApprovals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Pending Approvals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingApprovals.map(req => (
                    <div key={req.id} className="flex items-center justify-between p-4 border rounded-lg bg-amber-50">
                      <div className="flex items-center gap-4">
                        <Avatar>
                          <AvatarFallback>{req.user?.first_name?.[0]}{req.user?.last_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{req.user?.first_name} {req.user?.last_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {req.request_type} • {req.total_days} days • {format(new Date(req.start_date), 'MMM d')} - {format(new Date(req.end_date), 'MMM d')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleApproveTimeOff(req.id, false)}>
                          Reject
                        </Button>
                        <Button size="sm" onClick={() => handleApproveTimeOff(req.id, true)}>
                          Approve
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">User Management & Security</h2>
            <Dialog open={showNewUserDialog} onOpenChange={setShowNewUserDialog}>
              <DialogTrigger asChild>
                <Button data-testid="new-user-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>Create a new CRM user account</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select
                      value={newUser.role}
                      onValueChange={(val) => setNewUser({ ...newUser, role: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="sales_manager">Sales Manager</SelectItem>
                        <SelectItem value="sales_rep">Sales Rep</SelectItem>
                        <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                        <SelectItem value="hr_manager">HR Manager</SelectItem>
                        <SelectItem value="support_agent">Support Agent</SelectItem>
                        <SelectItem value="technical_support">Technical Support</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input
                      value={newUser.job_title}
                      onChange={(e) => setNewUser({ ...newUser, job_title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Select
                      value={newUser.department_id}
                      onValueChange={(val) => setNewUser({ ...newUser, department_id: val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map(dept => (
                          <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Hierarchy Level</Label>
                    <Select
                      value={newUser.hierarchy_level_id}
                      onValueChange={(val) => setNewUser({ ...newUser, hierarchy_level_id: val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {hierarchyLevels.map(level => (
                          <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewUserDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateUser}>Create User</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* User Seats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                User Seats
              </CardTitle>
              <CardDescription>Manage CRM user access and permissions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {userSeats.map(seat => (
                  <div key={seat.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <Avatar>
                        <AvatarFallback>{seat.user?.first_name?.[0]}{seat.user?.last_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{seat.user?.first_name} {seat.user?.last_name}</p>
                        <p className="text-sm text-muted-foreground">{seat.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{seat.role}</Badge>
                      <Badge className={seat.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {seat.status}
                      </Badge>
                      {seat.status === 'active' ? (
                        <Button variant="outline" size="sm" onClick={() => handleSuspendUser(seat.user_id)}>
                          Suspend
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => handleReactivateUser(seat.user_id)}>
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {userSeats.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No user seats found</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Audit Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit History
              </CardTitle>
              <CardDescription>Full history of user actions in the system</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {auditLogs.map(log => (
                  <div key={log.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded text-sm">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">{log.user?.first_name?.[0]}{log.user?.last_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{log.user?.first_name} {log.user?.last_name}</span>
                      <Badge variant="outline" className="text-xs">{log.action}</Badge>
                      <span className="text-muted-foreground">{log.entity_type}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                    </span>
                  </div>
                ))}
                {auditLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No audit logs</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HR Inbox Tab */}
        <TabsContent value="inbox" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                HR Inbox
              </CardTitle>
              <CardDescription>Action items requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {hrInbox.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-2 rounded-full ${
                        item.priority === 'urgent' ? 'bg-red-500' :
                        item.priority === 'high' ? 'bg-amber-500' :
                        'bg-blue-500'
                      }`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.title}</p>
                          <Badge variant="outline" className="text-xs">{item.item_type.replace('_', ' ')}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                        {item.related_user && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Related: {item.related_user.first_name} {item.related_user.last_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        item.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {item.status}
                      </Badge>
                      <Button variant="outline" size="sm">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {hrInbox.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No items in inbox</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Alerts Tab */}
        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Performance Alerts
              </CardTitle>
              <CardDescription>Notifications about team performance issues</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {performanceAlerts.map(alert => (
                  <div key={alert.id} className={`p-4 border rounded-lg ${
                    alert.severity === 'critical' ? 'border-red-200 bg-red-50' :
                    alert.severity === 'high' ? 'border-amber-200 bg-amber-50' :
                    'border-slate-200'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            alert.severity === 'high' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {alert.severity}
                          </Badge>
                          <span className="font-medium">{alert.title}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{alert.description}</p>
                        {alert.user && (
                          <p className="text-xs mt-2">
                            Employee: {alert.user.first_name} {alert.user.last_name}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline">{alert.status}</Badge>
                    </div>
                  </div>
                ))}
                {performanceAlerts.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No active alerts</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
